<template>
  <v-card class="white elevation-3 ma-4 duplicate-incidents">
    <v-subheader>JSON Uploading</v-subheader>
    <json-uploader :clients="clients"/>
    <v-divider />
    <v-subheader>Duplicate Incidents</v-subheader>
    <v-row class="pl-4 pr-4">
      <v-col
        cols="12"
        sm="2"
      >
        <v-select
          v-model="filters.clientId"
          :items="clientFilterItems"
          :label="$t('duplicateIncidents.filters.selectClient')"
          :menu-props="{'minWidth': 260, 'maxWidth': 260}"
          item-value="registryId"
          item-text="name"
          single-line
        />
      </v-col>
      <v-col
          cols="12"
          sm="2"
      >
        <v-text-field
            v-model="filters.sequenceNumber"
            :label="$t('duplicateIncidents.filters.sequenceNumber')"
            single-line
        />
      </v-col>
    </v-row>

    <v-tabs v-model="activeTab">
      <v-tab
        :key="'tab-new'"
      >
        New Duplicates
      </v-tab>
      <v-tab
        :key="'tab-log'"
      >
        Duplicates Log
      </v-tab>
    </v-tabs>
    <v-divider class="mb-5"/>

    <v-tabs-items v-model="activeTab">
      <v-tab-item>
        <duplicate-incidents-table
          :items="newItems"
          :pagination="newPagination"
          :total-items="newPagination.totalItems"
          :loading="newLoading"
          @showIncident="showNewIncident"
          @update:pagination="updateNewPagination"
        />
      </v-tab-item>

      <v-tab-item>
        <duplicate-incident-logs-table
          :items="logItems"
          :pagination="logPagination"
          :total-items="logPagination.totalItems"
          :loading="logLoading"
          @showIncident="showLogIncident"
          @update:pagination="updateLogPagination"
        />
      </v-tab-item>
    </v-tabs-items>
    <duplicate-incident-popup
      ref="duplicateIncidentPopup"
      @showDiff="showDiff"
      @closeAfterAction="reloadItems"
    />
    <diff-popup
      ref="diffPopup"
      @close="showIncident"
    />
  </v-card>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
import _ from 'lodash';
import { SET_TITLE } from '@/store/mutations';
import {
  LOAD_DUPLICATE_INCIDENTS,
  LOAD_DUPLICATE_INCIDENTS_LOG,
} from '@/store/DuplicateIncidents/actions';
import {
  SET_DUPLICATE_INCIDENTS_FILTERS,
} from '@/store/DuplicateIncidents/mutations';
import _debounce from 'lodash/debounce';
import DuplicateIncidentsTable from '../components/DuplicateIncidents/DuplicateIncidentsTable';
import DuplicateIncidentLogsTable from '../components/DuplicateIncidents/DuplicateIncidentLogsTable';
import DuplicateIncidentPopup from '../components/DuplicateIncidents/DuplicateIncidentPopup';
import JsonUploader from '../components/DuplicateIncidents/JsonUploader';
import DiffPopup from '../components/DuplicateIncidents/DiffPopup';
import apiClients from '../api/clients';

const { mapState, mapActions, mapMutations } = createNamespacedHelpers(
  'duplicateIncidents',
);

export default {
  components: {
    DuplicateIncidentPopup,
    DiffPopup,
    DuplicateIncidentsTable,
    DuplicateIncidentLogsTable,
    JsonUploader,
  },
  data() {
    return {
      newLoading: false,
      logLoading: false,
      newDebounceFunc: null,
      logDebounceFunc: null,
      clients: [],
      activeTab: 0,
      newItems: [],
      newPagination: {
        page: 1,
        sortDesc: [true],
        itemsPerPage: 25,
        sortBy: ['incidentDataUpdatedAt'],
        totalItems: 0,
      },
      logItems: [],
      logPagination: {
        page: 1,
        sortDesc: [true],
        itemsPerPage: 25,
        sortBy: ['incidentDataUpdatedAt'],
        totalItems: 0,
      },
    };
  },
  computed: {
    ...mapState({
      entityState: state => state,
    }),

    /**
     * Items for clients filter.
     *
     * @return {Array}
     */
    clientFilterItems() {
      const items = [];
      items.push({
        registryId: null,
        name: this.$t('duplicateIncidents.filters.allClients'),
      });
      return [...items, ...this.clients];
    },

    // Removed pagination computed property as we now use separate pagination objects for each table

    /**
     * Filter params.
     *
     * @return {Object}
     */
    filters() {
      return this.entityState.filters;
    },
  },
  watch: {
    filters: {
      handler(newVal) {
        this.newPagination.page = 1;
        this.logPagination.page = 1;
        this.setFilters(newVal);
        this.reloadNewItems();
        this.reloadLogItems();
      },
      deep: true,
    },
  },
  mounted() {
    if (this.$route.query.clientId || this.$route.query.sequenceNumber) {
      this.setFilters({
        clientId: this.$route.query.clientId ? parseInt(this.$route.query.clientId, 10) : null,
        sequenceNumber: this.$route.query.sequenceNumber ? this.$route.query.sequenceNumber : null,
      });
    } else {
      this.setFilters({
        clientId: null,
        sequenceNumber: null,
      });
    }
    // Initialize both tables
    this.newItems = [];
    this.logItems = [];

    // Load data for both tables
    this.reloadNewItems();
    this.reloadLogItems();

    this.$store.commit(SET_TITLE, this.$t('duplicateIncidents.header'));
    this.loadClients();
  },
  methods: {
    ...mapMutations({
      setFilters: SET_DUPLICATE_INCIDENTS_FILTERS,
    }),
    ...mapActions({
      loadItems: LOAD_DUPLICATE_INCIDENTS,
      loadLogItems: LOAD_DUPLICATE_INCIDENTS_LOG,
    }),

    updateNewPagination(newPagination) {
      if (!_.isEqual(newPagination, this.newPagination)) {
        this.newPagination = newPagination;
        this.reloadNewItems();
      }
    },

    updateLogPagination(newPagination) {
      if (!_.isEqual(newPagination, this.logPagination)) {
        this.logPagination = newPagination;
        this.reloadLogItems();
      }
    },

    /**
     * Reloads new incidents paginated list for current filters
     */
    async reloadNewItems() {
      if (!this.newDebounceFunc) {
        const that = this;
        this.newDebounceFunc = _debounce(async () => {
          that.newLoading = true;
          try {
            const results = await that.loadItems(that.newPagination);
            that.newItems = results.results;
            that.newPagination.totalItems = results.pagination.total;
          } finally {
            that.newLoading = false;
          }
        }, 300);
      }
      this.newDebounceFunc();
    },

    /**
     * Reloads log incidents paginated list for current filters
     */
    async reloadLogItems() {
      if (!this.logDebounceFunc) {
        const that = this;
        this.logDebounceFunc = _debounce(async () => {
          that.logLoading = true;
          try {
            const results = await that.loadLogItems(that.logPagination);
            that.logItems = results.results;
            that.logPagination.totalItems = results.pagination.total;
          } finally {
            that.logLoading = false;
          }
        }, 300);
      }
      this.logDebounceFunc();
    },

    /**
     * Reloads incidents for the active tab
     */
    reloadItems() {
      this.reloadNewItems();
      this.reloadLogItems();
    },

    /**
     * Show compare incidents popup for new incidents.
     *
     * @param {Object} incident - Duplicated incident object.
     */
    showNewIncident(incident) {
      this.$refs.duplicateIncidentPopup.show(incident);
    },

    /**
     * Show compare incidents popup for log incidents.
     *
     * @param {Object} incident - Duplicated incident object.
     */
    showLogIncident(incident) {
      this.$refs.duplicateIncidentPopup.show(incident);
    },

    /**
     * Show diff incidents popup.
     *
     * @param {Object} incident - Duplicated incident object.
     */
    showDiff(incident) {
      this.$refs.diffPopup.show(incident);
    },

    /**
     * Load clients list.
     */
    async loadClients() {
      try {
        const clients = await apiClients.fetchList({ type: 'default' });
        this.clients = clients.results;
      } catch (e) {
        this.clients = [];
      }
    },
  },
};
</script>
